import { Injectable, inject } from '@angular/core';
import Survicate from '@survicate/survicate-web-package/survicate_widget';
import type {SurveyPointInfo, SurveyType} from '@survicate/survicate-web-package/survicate_widget';
import {AppConfig, APPCONFIG, FeatureFlags, FeatureFlagService, SessionService} from "@core";
import {catchError, defaultIfEmpty, filter, map, shareReplay, switchMap, take, tap} from "rxjs/operators";
import {TranslateService} from '@ngx-translate/core';
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {defer, EMPTY, from, fromEventPattern, Observable, of} from "rxjs";
import {NotificationService} from "@shared";

@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
export class SurvicateService {
    private readonly sessionService = inject(SessionService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly translateService = inject(TranslateService);
    private readonly notificationService = inject(NotificationService);
    private readonly appConfig = inject<AppConfig>(APPCONFIG);

    private sdkInit$: Observable<void> | null = null;
    private readonly survicateEnabled$: Observable<boolean>;

    constructor() {

        this.survicateEnabled$ = this.featureFlagService
            .isEnabled(FeatureFlags.SurvicateCsat)
            .pipe(shareReplay(1));
    }

    public init(): void {
        this.survicateEnabled$
            .pipe(
                take(1),
                filter(isEnabled => isEnabled),
                switchMap(() => this.ensureInitialized())
            )
            .subscribe();
    }

    public tryShowSurveyAuto(): Observable<boolean> {
        return this.survicateEnabled$.pipe(
            switchMap(isEnabled => {
                if (!isEnabled) {
                    return of(false);
                }
                return this.ensureInitialized().pipe(
                    tap(() => {
                        this.refreshVisitorTraits();
                        Survicate.invokeEvent('showSurvey');
                    }),
                    map(() => true),
                    defaultIfEmpty(false)
                );
            })
        );
    }

    public showSurveyAuto(): void {
        this.ensureInitialized().pipe(take(1)).subscribe(() => {
            this.refreshVisitorTraits();
            Survicate.invokeEvent('showSurvey');
        });
    }

    public submitCsatResponse(score: string, text: string): void {
        this.ensureInitialized().pipe(
            take(1),
            map(() => true as const),
            defaultIfEmpty(false as const)
        ).subscribe(ready => {
            if (!ready) {
                this.notificationService.error("CSAT_ERROR");
                return;
            }

            try {
                this.refreshVisitorTraits();

                const surveyId = this.appConfig.survicateSurveyId;
                const points: SurveyPointInfo[] | null = Survicate.getSurveyPointsMetadata(surveyId);

                const scorePoint = points.find(p =>
                    p.answerType === 'rating'
                );

                const textPoint = points.find(p =>
                    p.answerType === 'text'
                );

                const responseUuid = Survicate.getResponseUuid('WidgetSurvey' as SurveyType, true);

                if (scorePoint) {
                    const answerId = scorePoint.answers[Number(score) - 1].id;
                    Survicate.submitAnswer({
                        surveyId,
                        pointId: scorePoint.pointId,
                        answer: answerId,
                    }, responseUuid);
                }

                if (textPoint && text) {
                    Survicate.submitAnswer({
                        surveyId,
                        pointId: textPoint.pointId,
                        answer: text
                    }, responseUuid);
                }

                this.notificationService.success("CSAT_SUCCESS");
            } catch {
                this.notificationService.error("CSAT_ERROR");
            }
        });
    }

    private normalizeLanguageTag(lang: string): string {
        if (!lang) {
            return 'en';
        }

        const parts = lang.split('-');

        if (parts[0] === "en") {
            return parts[0];
        } else {
            return parts[1];
        }
    }

    private ensureInitialized(): Observable<void> {
        if (!this.sdkInit$) {
            const initialLanguage = this.normalizeLanguageTag(this.translateService.currentLang);
            this.sdkInit$ = defer(() => from(Survicate.init({
                workspaceKey: this.appConfig.survicateWorkspaceKey,
                forcedLanguage: initialLanguage
            }))).pipe(
                tap(() => this.subscribeToSurveyDisplayed()),
                catchError(() => {
                    this.sdkInit$ = null;
                    return EMPTY;
                }),
                shareReplay(1)
            );
        }
        return this.sdkInit$;
    }

    private subscribeToSurveyDisplayed(): void {
        fromEventPattern<void>(
            handler => Survicate.addEventListener(Survicate.ApiEvent.surveyDisplayed, handler as () => void),
            (_handler, eventId: number) => Survicate.removeEventListener(eventId)
        )
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                const languageTag = this.normalizeLanguageTag(this.translateService.currentLang);
                Survicate.setSurveyLanguage(languageTag);
            });
    }

    private refreshVisitorTraits(): void {
        Survicate.setVisitorTraits({
            municipality: this.sessionService.municipalityName,
            user_id: this.sessionService.userName,
            request_id: this.sessionService.requestId
        });
    }
}
